import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { LayoutDashboard, Plus, Maximize2 } from "lucide-react";

export const Interface = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Understanding the Interface</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Overview of Nordex's trading interface and panel system</p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Overview</h2>
          <p className="text-muted-foreground mb-6">
            Nordex features a flexible, panel-based interface that allows you to customize your trading workspace. 
            You can add, remove, resize, and rearrange panels to create a layout that works best for your trading style.
          </p>
        </section>

        {/* Main Components */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Main Interface Components</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Header</h3>
              <p className="text-sm text-muted-foreground mb-2">
                The header contains essential navigation and account controls:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
                <li><strong className="text-foreground">Logo:</strong> Click to return to the main trading page</li>
                <li><strong className="text-foreground">Market Selector:</strong> Switch between different perpetual markets</li>
                <li><strong className="text-foreground">Navigation Links:</strong> Access Account, Referrals, and Affiliate pages</li>
                <li><strong className="text-foreground">Wallet/Balance:</strong> View your current balance and account status</li>
                <li><strong className="text-foreground">Deposit Button:</strong> Quick access to deposit USDC</li>
                <li><strong className="text-foreground">Settings:</strong> Adjust trading preferences and theme</li>
              </ul>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Trading Workspace</h3>
              <p className="text-sm text-muted-foreground">
                The main area where all trading panels are displayed. Panels can be resized by dragging 
                their borders and rearranged by dragging panel tabs.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Status Bar (Desktop)</h3>
              <p className="text-sm text-muted-foreground">
                Located at the bottom of the page on desktop, showing connection status and quick links 
                to Support, Documentation, Terms, and Privacy Policy.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Bottom Navigation (Mobile)</h3>
              <p className="text-sm text-muted-foreground">
                On mobile devices, the bottom navigation provides quick access to Markets, Trade, and Account views.
              </p>
            </div>
          </div>
        </section>

        {/* Available Panels */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Available Panels</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Order Form</h3>
              <p className="text-sm text-muted-foreground">
                The primary panel for placing trades. Enter your collateral amount, select leverage, 
                and open long or short positions. Shows real-time position size calculations and 
                liquidation prices.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Positions</h3>
              <p className="text-sm text-muted-foreground">
                View and manage all your open positions. Shows entry price, current price, unrealized P&L, 
                leverage, and liquidation price for each position. Click the close button to exit positions.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Balances (Account)</h3>
              <p className="text-sm text-muted-foreground">
                Displays your account summary including Total Collateral, Available Balance, Used Margin, 
                Unrealized P&L, Total Equity, Account Leverage, and Margin Ratio. Essential for monitoring 
                account health.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Order Book</h3>
              <p className="text-sm text-muted-foreground">
                Live order book showing bid and ask prices with their quantities. Updated in real-time 
                via real-time data feeds from professional oracle providers. Useful for understanding market depth and support/resistance levels.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Market Chart</h3>
              <p className="text-sm text-muted-foreground">
                Integrated TradingView candlestick chart with full technical analysis capabilities. 
                Includes drawing tools, indicators, and multiple timeframes.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Market Trades</h3>
              <p className="text-sm text-muted-foreground">
                Live feed of recent market trades showing price, size, and timestamp. Color-coded 
                (green for buys, red for sells) to show market sentiment.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Funding Rate</h3>
              <p className="text-sm text-muted-foreground">
                Displays current funding rate for the selected perpetual market and time until next 
                funding payment. Includes explanation of how funding works.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Price Chart</h3>
              <p className="text-sm text-muted-foreground">
                Historical price chart showing price movement over time. Useful for quick price 
                trend analysis without the full TradingView interface.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Volume Chart</h3>
              <p className="text-sm text-muted-foreground">
                Historical volume data visualization. Helps identify periods of high trading activity 
                and potential trend changes.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Favorites</h3>
              <p className="text-sm text-muted-foreground">
                Quick access to your favorite markets. Star markets to add them to this panel 
                for fast switching.
              </p>
            </div>
          </div>
        </section>

        {/* Customizing Your Layout */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Customizing Your Layout</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Adding Panels</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Click the "+" button in the header to open the panel selector. Choose from available 
                panels to add them to your workspace.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <LayoutDashboard className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg">Rearranging Panels</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Drag panel tabs to reorder them. On desktop, you can also resize panels by dragging 
                the borders between them.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Maximize2 className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="font-semibold text-lg">Maximizing Panels</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Some panels (like Market Chart) have a maximize button. Click it to expand the panel 
                to full screen for detailed analysis.
              </p>
            </div>
          </div>
        </section>

        {/* Layout Presets */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Multiple Layouts</h2>
          
          <p className="text-muted-foreground mb-4">
            Nordex allows you to create and save multiple layout configurations:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>
              Use the layout selector in the header to switch between different saved layouts
            </li>
            <li>
              Create specialized layouts for different trading scenarios (e.g., "Scalping", "Analysis", "Multi-market")
            </li>
            <li>
              Layouts are automatically saved to your browser's local storage
            </li>
            <li>
              Each layout remembers which panels are visible and how they're arranged
            </li>
          </ul>
        </section>

        {/* Mobile Interface */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Mobile Interface</h2>
          
          <p className="text-muted-foreground mb-4">
            On mobile devices, the interface is optimized for smaller screens:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>
              <strong className="text-foreground">Markets View:</strong> Browse and switch between different perpetual markets
            </li>
            <li>
              <strong className="text-foreground">Trade View:</strong> Focused trading interface with Order Form, chart, and order book
            </li>
            <li>
              <strong className="text-foreground">Account View:</strong> Access your portfolio, positions, balances, and transaction history
            </li>
            <li>
              Bottom navigation makes it easy to switch between these three main views
            </li>
          </ul>
        </section>

        {/* Tips */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Interface Tips</h2>
          
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Start with the default layout</strong> -- gradually customize it as you 
              become more familiar with the platform. Everyone's trading style is different.
            </li>
            <li>
              <strong className="text-foreground">Keep essential panels visible</strong> -- the Order Form, Positions, and Balances panels should always be accessible 
              for easy trading.
            </li>
            <li>
              <strong className="text-foreground">Maximize for analysis</strong> -- use the maximize feature on the Market Chart panel when you need 
              detailed technical analysis.
            </li>
            <li>
              <strong className="text-foreground">Reset layout</strong> -- if you ever want to start fresh, remove all panels 
              and add them back in your preferred order.
            </li>
          </ul>
        </section>

        <DocsNavigation 
          previous={{ title: "Placing Your First Trade", href: "/first-trade" }}
          next={{ title: "Understanding Perpetual Futures", href: "/perpetuals" }}
        />
      </div>
    </DocsLayout>
  );
};

